import { useState, useEffect, useMemo } from 'react'

interface HookOptions {
  ref: React.RefObject<HTMLElement | null>
}

/**
 * Hook that returns actual dimensions (width and height) of a referenced HTML element.
 */
export const useElementDimensions = ({ ref }: HookOptions) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === ref.current) {
            setDimensions({
              width: ref.current.offsetWidth,
              height: ref.current.offsetHeight,
            })
          }
        })
      }),
    [ref],
  )

  useEffect(() => {
    let currentRef = ref.current

    if (currentRef) {
      resizeObserver.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        resizeObserver.unobserve(currentRef)
      }
    }
  }, [ref, resizeObserver])

  return dimensions
}
